import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Treemap } from 'recharts';

import TitleCase from "components/formatting/TitleCase";
import { exampleWords } from "../WordCloud/WordCloudPreview";
import TreeMapContent from "./TreeMapNode";
import { treeMapDefaultSettings, TreeMapSettingsStateType } from "./treeMapUtils";

interface TopicsTreeMapPreviewProps {
  /**
   * State object represented by series of key value pairs,
   * where the key is a string which represents the title of the
   * updatable settings option.
   */
  settingsState: TreeMapSettingsStateType | {};
}

export default function TopicsTreeMapPreview(props: TopicsTreeMapPreviewProps) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentState = {...treeMapDefaultSettings, ...props.settingsState};
  const previewRef = useRef<HTMLDivElement | null | undefined>();
  const [previewWidth, setPreviewWidth] = useState<number>(0);
  const [previewHeight, setPreviewHeight] = useState<number>(0);
  const COLORS = theme.colors.namedColors.chartColorArray;

  let treeExampleWords = [];
  const maxTopicCount = 25; // For performance issues: don't render more than 25 words in preview
  const topicsToRender = currentState.topicCount > maxTopicCount ? maxTopicCount : currentState.topicCount;
  
  for (let i = 0; i < topicsToRender; i++) {
    treeExampleWords.push(exampleWords[i % exampleWords.length]);
  }

  let exampleTopics = useMemo(() => {
    return treeExampleWords.map((word, index) => {
      return {
        cluster_id: (index % 7) + 1,
        count: Math.floor(Math.random() * 100),
        label: word,
        relevance: Math.floor(Math.random() * 100),
      }
    });
  }, [currentState.topicCount]);

  let formattedData = useMemo(() => {
    // Clustering bias currently doesn't work the way it does in the widget - not relevant to preview.
    // Keeping this in case we want to implement realistic clustering bias later.
    const clusters: any = { };
    // Group topics by cluster
    exampleTopics.forEach((topic: any) => {
      if (!clusters[topic.cluster_id]) {
        clusters[topic.cluster_id] = [];
      }
      clusters[topic.cluster_id].push({
        name: topic.label, 
        size: topic.relevance ? topic.relevance : topic.count,
        topic: topic
      });
    });

    const treemapData: any = [];
    // Convert to treemap data format
    Object.keys(clusters).forEach((clusterId: string) => {
      const cluster = clusters[clusterId];
      treemapData.push({
        name: clusterId,
        children: cluster
      });
    });

    return treemapData;
  }, [exampleTopics, currentState.clusteringBias]);

  useEffect(() => {
    if (previewRef && previewRef.current) {
      setPreviewWidth(previewRef.current.clientWidth);
      setPreviewHeight(previewRef.current.clientHeight);
    }
  }, [previewWidth, previewHeight]);

  return (
    <Stack sx={{
      width: isSmallScreen ? '100%' : '50%'
    }}>
      {currentState.widgetTitle !== 'none' &&
        <Typography
          sx={{ fontSize: "1.2rem", pb: 2, textAlign: `${currentState.widgetTitleAlignment}` }}
          variant="subtitle2"
        >
          <TitleCase text={currentState.widgetTitle} />
        </Typography>}
      <Box ref={previewRef} sx={{ width: "100%", height: "100%", minHeight: "35vh" }}>
        <Treemap
          animationDuration={200}
          animationEasing="ease-out"
          aspectRatio={16 / 9}
          content={<TreeMapContent colors={COLORS} data={formattedData} />}
          data={formattedData}
          dataKey="size"
          fill="#8884d8"
          height={previewHeight}
          stroke="#fff"
          width={previewWidth} />
      </Box>
    </Stack>
  );
}
